import FJUtil from '../../../util/FJUtil';
import styles from './FJVideoPreviewBox.module.less';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import PlayIcon from '../../../assets/svg/play-black.svg?react';
import PauseIcon from '../../../assets/svg/pause-black.svg?react';
import FJSlider from '../FJSlider/FJSlider';

const FJVideoPreviewBox = props => {
    const { videoInfo, style, className } = props;
    const { width, height, src, start = 0, end } = videoInfo;

    const [videoCurrentTime, setVideoCurrentTime] = useState(0);
    const [isShowPreviewBox, setIsShowPreviewBox] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const isPlayingRef = useRef(false);
    const videoRef = useRef(null);
    const timer = useRef(null);
    const previewBoxRef = useRef(null);

    const previewSize = useMemo(() => {
        if (!width || !height || !src) return;
        return FJUtil.getPreviewSize({ width, height });
    }, [width, height, src]);

    const playVideo = () => {
        setIsPlaying(true);
        isPlayingRef.current = true;
        let currentTime = Math.max(videoRef.current.currentTime - start, 0);

        if (videoRef.current.currentTime < start || videoRef.current.currentTime > end) {
            videoRef.current.currentTime = start;
            currentTime = 0;
        }

        setVideoCurrentTime(currentTime);
        videoRef.current.play();
        const duration = end - start;

        let requestAnimationTime = null;
        let __playing = timestamp => {
            if (!requestAnimationTime) requestAnimationTime = timestamp;

            currentTime += (timestamp - requestAnimationTime) / 1000;
            requestAnimationTime = timestamp;

            if (currentTime >= 0 && currentTime <= duration) {
                setVideoCurrentTime(currentTime);
            }

            if (currentTime >= duration) {
                currentTime = 0;
                videoRef.current.currentTime = start;
                setVideoCurrentTime(currentTime);
            }
            timer.current = window.requestAnimationFrame(__playing);
        };

        if (timer.current) window.cancelAnimationFrame(timer.current);
        timer.current = window.requestAnimationFrame(__playing);
    };

    const pauseVideo = () => {
        setIsPlaying(false);
        window.cancelAnimationFrame(timer.current);
        isPlayingRef.current = false;
        videoRef.current.pause();
    };

    const hidePreviewBox = e => {
        if (previewBoxRef.current?.contains(e.target)) return;
        pauseVideo();
        videoRef.current.currentTime = start;
        setVideoCurrentTime(0);
        setIsShowPreviewBox(false);
        window.removeEventListener('mousedown', hidePreviewBox);
    };

    const showPreviewBox = () => {
        setIsShowPreviewBox(true);
        playVideo();
        window.addEventListener('mousedown', hidePreviewBox);
    };

    const _onClick = e => {
        e.stopPropagation();
        showPreviewBox();
    };

    const seekVideo = value => {
        const canPlay = isPlayingRef.current;
        pauseVideo();
        setVideoCurrentTime(value);
        videoRef.current.currentTime = value + start;
        if (canPlay) playVideo();
    };

    const videoOnClick = e => {
        e.stopPropagation();
        if (isPlaying) {
            pauseVideo();
        } else {
            playVideo();
        }
    };

    return (
        <div className={classNames(styles.hover_box, className)} onClick={_onClick}>
            {previewSize && (
                <div
                    onClick={e => e.stopPropagation()}
                    ref={previewBoxRef}
                    className={classNames(styles.preview_box, isShowPreviewBox && styles.active)}
                    style={{ ...style }}
                >
                    <video
                        onClick={videoOnClick}
                        className={styles.preview_video}
                        style={{ width: previewSize.width, height: previewSize.height }}
                        src={src}
                        alt=''
                        loading='lazy'
                        ref={videoRef}
                        loop
                    />
                    <div className={styles.control_box}>
                        {isPlaying ? <PauseIcon onClick={pauseVideo} /> : <PlayIcon onClick={playVideo} />}
                        <FJSlider value={videoCurrentTime} min={0} max={end - start} onChange={seekVideo} />
                        <div className={styles.time_box}>
                            <span className={styles.time_text}>{FJUtil.formatTime(videoCurrentTime)}</span>
                            <span className={styles.all_time_text}> / {FJUtil.formatTime(end - start)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FJVideoPreviewBox;
